import React from 'react';
import capitalize from 'lodash/capitalize';
import { Text } from '@atlaskit/primitives';
import { DynamicResultTable, FormattedDate } from '@css-xp/base';
import { CloudProductDetailView, EntitlementStatus } from '@css-xp/base/entitlement-utils';
import {
  BillingStatus,
  LinkedDestinationEntitlement,
  LinkedEntitlementTypes,
  LinkedProductTypes,
} from '@css-xp/graphtypes/src/generated-types-new';
import {
  errorStateProp,
  emptyStateProp,
  NoData,
  TableHeading,
  genericEmptyStateProp,
  FormatCurrency,
} from '@css-xp/ppc-common';
import { getLatestPeriodEndDate } from '@css-xp/entitlements-v2';
import {
  getAddOnQuantity,
  getChargeElementText,
  isFutureDatedInactiveSubscription,
} from '@css-xp/ppc-utils/src/ppc-product-utils';
import { getRouteUrl } from '@css-xp/ppc-utils';
import { routePaths } from '@css-xp/ppc-constants';
import { fetchAddOns } from '../../../queries/fetch-add-ons';
import { useFetchLinkedEntitlementsPPCQuery } from '../../../hooks/use-fetch-linked-entitlements-ppc-query';
import { isStatuspage } from '@atlassian/cssxp-utils-product';

export interface LinkedEntitlementsProps {
  id: string | undefined;
  transactionAccountId?: string | null;
  currentEntitlementType?: LinkedEntitlementTypes;
  productType: LinkedProductTypes;
  entitlementId: string;
  baseProduct?: string;
  isDesignIteration1Enabled?: boolean;
  isFutureDatedEntitlementEnabled?: boolean;
}

export const AddOnsTab = ({
  id,
  transactionAccountId,
  currentEntitlementType,
  productType,
  entitlementId,
  baseProduct,
  isDesignIteration1Enabled = false,
  isFutureDatedEntitlementEnabled = false,
}: LinkedEntitlementsProps) => {
  const subscriptionsRoutePPC =
    transactionAccountId && getRouteUrl(routePaths.subscriptions, { txaId: transactionAccountId });

  const { data, loading, error } = useFetchLinkedEntitlementsPPCQuery({
    entitlementId: entitlementId,
    containerId: id,
    currentEntitlementType: currentEntitlementType,
    destinationEntitlementType: [LinkedEntitlementTypes.PROD_ENTITLEMENT_ADDON],
    productType: productType,
    findProducts: false,
    findApps: false,
    transactionAccountId: transactionAccountId,
    query: fetchAddOns,
  });

  const linkedAddOns: Array<LinkedDestinationEntitlement> = data?.entitlementDetails
    ?.linkedEntitlements as Array<LinkedDestinationEntitlement>;

  const totalCount = linkedAddOns?.length || 0;
  const getRows = () => {
    return linkedAddOns
      ?.filter((entitlement) => entitlement?.entitlementId !== entitlementId)
      ?.filter((entitlement) => (baseProduct ? entitlement?.baseProduct === baseProduct : true))
      ?.map((item, index) => {
        const userTier = item?.userTier ?? 0;
        const activeUsers = item?.activeUsers ?? 0;
        const chargeElement = item?.chargeElement;
        const isStatusPageAddOn = isStatuspage(item?.productId);
        const productDetailView = (
          <CloudProductDetailView
            productId={item?.productId as string}
            entitlementNumber={item?.entitlementNumber ?? ''}
            productLink={`${subscriptionsRoutePPC}${item?.entitlementId}`}
            productName={isStatusPageAddOn ? item?.offering?.name : item?.productName}
            trial={item?.trial ?? false}
            provisioningStatus={item?.provisioningStatus}
            isAddOn={true}
            shouldHideExtraInfo={isDesignIteration1Enabled}
            version={isDesignIteration1Enabled ? 2 : undefined}
            isStatusPageAddOn={isStatusPageAddOn}
          />
        );

        const isActive = item?.billingStatus === BillingStatus.ACTIVE;
        const addOnBillingFrequency = item?.billingFrequency ?? item?.billingCycle;

        const addOnPlan =
          isActive && addOnBillingFrequency ? (
            <div>
              <div>Add-on</div>
              {isDesignIteration1Enabled ? (
                <Text color="color.text.subtlest" size="small">
                  {capitalize(addOnBillingFrequency)}
                </Text>
              ) : (
                <div className="n-300">{capitalize(addOnBillingFrequency)}</div>
              )}
            </div>
          ) : (
            <NoData />
          );

        const quantity = (
          <div>
            <div>{getAddOnQuantity(addOnBillingFrequency, activeUsers, userTier)}</div>
            {isDesignIteration1Enabled ? (
              <Text color="color.text.subtlest" size="small">
                {capitalize(getChargeElementText(chargeElement))}
              </Text>
            ) : (
              <div className="n-300">{capitalize(getChargeElementText(chargeElement))}</div>
            )}
          </div>
        );

        const createdDate = item?.createdDate ? <FormattedDate date={item?.createdDate} /> : <NoData />;

        const latestPeriodEndDate = item?.latestPeriodEndDate ? (
          getLatestPeriodEndDate(item?.billingStatus, item?.latestPeriodEndDate)
        ) : (
          <NoData />
        );

        const nextPriceEstimate = (
          <FormatCurrency
            value={item?.nextBillEstimate?.total as number}
            currency={item?.nextBillEstimate?.currency}
            showCurrencyCode
            showCommas
          />
        );

        const isFutureDatedInactiveEntitlement =
          isFutureDatedEntitlementEnabled &&
          isFutureDatedInactiveSubscription(!!item?.isFutureDatedSubscription, item?.billingStatus as BillingStatus);

        const status = (
          <EntitlementStatus
            billingStatus={item.billingStatus}
            provisioningStatus={item?.provisioningStatus}
            isFutureDatedInactiveEntitlement={isFutureDatedInactiveEntitlement}
            hideInactiveStatus={isFutureDatedInactiveEntitlement}
          />
        );

        const cellsContent = (() => {
          const rowItemContent = isDesignIteration1Enabled
            ? [
                {
                  content: productDetailView,
                },
                {
                  content: status,
                },
                {
                  content: addOnPlan,
                },
                {
                  content: quantity,
                },
                {
                  content: createdDate,
                },
                {
                  content: latestPeriodEndDate,
                },
                {
                  content: nextPriceEstimate,
                },
              ]
            : [
                {
                  content: productDetailView,
                },
                {
                  content: addOnPlan,
                },
                {
                  content: status,
                },
                {
                  content: quantity,
                },
                {
                  content: createdDate,
                },
                {
                  content: latestPeriodEndDate,
                },
                {
                  content: nextPriceEstimate,
                },
              ];
          return rowItemContent;
        })();

        return {
          key: index.toString(),
          cells: cellsContent,
        };
      });
  };

  const LINKED_ENTITLEMENT_HEADER = {
    NAME: {
      content: 'Name',
      shouldTruncate: true,
      width: isDesignIteration1Enabled ? 8 : 25,
    },
    STATUS: {
      content: 'Status',
      width: isDesignIteration1Enabled ? 5 : 10,
    },
    PLAN: {
      content: 'Plan',
      width: isDesignIteration1Enabled ? 5 : 10,
    },
    USAGE: {
      content: 'Quantity',
      width: isDesignIteration1Enabled ? 8 : 15,
    },
    ADDED: {
      content: 'Date added',
      width: isDesignIteration1Enabled ? 5 : 10,
    },
    NEXT_BILL: {
      content: 'Next bill date',
      width: isDesignIteration1Enabled ? 8 : 10,
    },
    NEXT_PRICE_ESTIMATE: {
      content: 'Next price estimate',
      width: 10,
    },
  };

  const getHead = () => {
    const headerContent = (() => {
      if (isDesignIteration1Enabled) {
        if (totalCount === 0) {
          return [];
        }
        return [
          LINKED_ENTITLEMENT_HEADER.NAME,
          LINKED_ENTITLEMENT_HEADER.STATUS,
          LINKED_ENTITLEMENT_HEADER.PLAN,
          LINKED_ENTITLEMENT_HEADER.USAGE,
          LINKED_ENTITLEMENT_HEADER.ADDED,
          LINKED_ENTITLEMENT_HEADER.NEXT_BILL,
          LINKED_ENTITLEMENT_HEADER.NEXT_PRICE_ESTIMATE,
        ];
      }
      return [
        LINKED_ENTITLEMENT_HEADER.NAME,
        LINKED_ENTITLEMENT_HEADER.PLAN,
        LINKED_ENTITLEMENT_HEADER.STATUS,
        LINKED_ENTITLEMENT_HEADER.USAGE,
        LINKED_ENTITLEMENT_HEADER.ADDED,
        LINKED_ENTITLEMENT_HEADER.NEXT_BILL,
        LINKED_ENTITLEMENT_HEADER.NEXT_PRICE_ESTIMATE,
      ];
    })();
    return {
      cells: headerContent,
    };
  };

  return (
    <div className="statuspage-tab" data-testid="statuspage-add-ons">
      <DynamicResultTable
        testId="linked-entitlements"
        beforeTable={isDesignIteration1Enabled ? undefined : <TableHeading>Add-ons</TableHeading>}
        head={getHead()}
        rows={getRows()}
        emptyStateProps={isDesignIteration1Enabled ? genericEmptyStateProp : emptyStateProp}
        isLoading={loading}
        errorStateProps={errorStateProp}
        error={error?.message}
        totalCount={totalCount}
      />
    </div>
  );
};
